import { useLiveQuery } from 'dexie-react-hooks'
import { db, alive } from './db'
import { useEffect, useState } from 'react'
import { subscribe, type SyncState } from './sync'
import { BUILTIN_CATEGORIES, type CategoryDef, type User } from './types'

/** Everything reads from IndexedDB, online or not. No screen ever waits on the network. */

export function useTrip() {
  return useLiveQuery(async () => {
    const trips = alive(await db.trips.toArray())
    return trips.find((t) => !t.archived) ?? trips[0] ?? null
  }, [], null)
}

export function usePlaces(tripId?: string) {
  return useLiveQuery(async () => {
    if (!tripId) return []
    const rows = alive(await db.places.where('trip_id').equals(tripId).toArray())
    return rows.sort((a, b) => a.sort_order - b.sort_order)
  }, [tripId], [])
}

export function useItems(tripId?: string) {
  return useLiveQuery(async () => {
    if (!tripId) return []
    return alive(await db.items.where('trip_id').equals(tripId).toArray())
  }, [tripId], [])
}

export function useBookings(tripId?: string) {
  return useLiveQuery(async () => {
    if (!tripId) return []
    const rows = alive(await db.bookings.where('trip_id').equals(tripId).toArray())
    return rows.sort((a, b) => (a.starts_at ?? Infinity) - (b.starts_at ?? Infinity))
  }, [tripId], [])
}

export function useExpenses(tripId?: string) {
  return useLiveQuery(async () => {
    if (!tripId) return []
    const rows = alive(await db.expenses.where('trip_id').equals(tripId).toArray())
    return rows.sort((a, b) => b.spent_at - a.spent_at)
  }, [tripId], [])
}

/** Activities for one local day, in clock order. */
export function useDayItems(tripId: string | undefined, day: string) {
  return useLiveQuery(async () => {
    if (!tripId) return []
    const rows = alive(await db.items.where('trip_id').equals(tripId).toArray())
    return rows
      .filter((i) => i.day_date === day)
      .sort((a, b) => (a.starts_at ?? Infinity) - (b.starts_at ?? Infinity) || a.sort_order - b.sort_order)
  }, [tripId, day], [])
}

/** The distinct days a trip has activities on, ascending. */
export function useDays(tripId?: string) {
  return useLiveQuery(async () => {
    if (!tripId) return []
    const rows = alive(await db.items.where('trip_id').equals(tripId).toArray())
    return [...new Set(rows.map((i) => i.day_date).filter(Boolean) as string[])].sort()
    // The default has to be typed: a bare [] infers never[] and poisons every
    // caller with 'not assignable to never'.
  }, [tripId], [] as string[])
}

/**
 * Documents attached to one activity, booking or place. Metadata syncs, so this
 * works offline; only opening the file needs a connection.
 */
export function useAttachedDocuments(entityType: 'item' | 'booking' | 'place', entityId?: string) {
  return useLiveQuery(async () => {
    if (!entityId) return []
    const links = await db.document_attachments
      .where('entity_id').equals(entityId).toArray()
    const mine = links.filter((l) => l.entity_type === entityType)
    if (!mine.length) return []
    const docs = await db.documents.bulkGet(mine.map((l) => l.document_id))
    return docs.filter((d): d is NonNullable<typeof d> => !!d && !d.deleted_at)
  }, [entityType, entityId], [])
}

export function useDocuments() {
  return useLiveQuery(async () => alive(await db.documents.toArray()), [], [])
}

/**
 * The six built-ins followed by whatever the party added, in their own order.
 *
 * The built-ins come first and always: a custom category is an addition, never
 * a replacement, so nobody can delete their way into a ledger with nowhere to
 * put a meal.
 */
export function useCategories(tripId?: string): CategoryDef[] {
  return useLiveQuery(async () => {
    if (!tripId) return BUILTIN_CATEGORIES
    const rows = alive(await db.expense_categories.where('trip_id').equals(tripId).toArray())
    const custom = rows
      .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label))
      .map((r) => ({ id: r.id, label: r.label, icon: r.icon || '•' }))
    return [...BUILTIN_CATEGORIES, ...custom]
  }, [tripId], BUILTIN_CATEGORIES) ?? BUILTIN_CATEGORIES
}

/**
 * Naming one category, including one that has since been deleted. A deleted
 * category keeps its expenses; they show the raw id rather than quietly
 * becoming "Other", because moving somebody's money between buckets behind
 * their back is worse than an ugly label. Not a hook, so it can be used inside
 * a map.
 */
export const catOf = (all: CategoryDef[], id: string): CategoryDef =>
  all.find((c) => c.id === id) ?? { id, label: id, icon: '•' }

export function useShares() {
  return useLiveQuery(() => db.expense_shares.toArray(), [], [])
}

export function useUsers(): User[] {
  return useLiveQuery(() => db.users.toArray(), [], []) ?? []
}

export type AuthState = 'checking' | 'in' | 'out'

let cachedMe: User | null = null
let cachedState: AuthState = 'checking'

/**
 * Who is signed in, and whether we know yet. The 'out' state matters: without
 * it a signed-out visitor gets the app shell with no data in it, which reads
 * as broken rather than as "sign in".
 */
export function useMe(): User | null {
  return useAuth().me
}

export function useAuth(): { me: User | null; state: AuthState } {
  const [me, setMe] = useState<User | null>(cachedMe)
  const [state, setState] = useState<AuthState>(cachedState)

  useEffect(() => {
    let live = true
    fetch('/api/me', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(async (r) => {
        if (r.status === 401 || r.status === 403) return { out: true as const }
        if (!r.ok || !r.headers.get('content-type')?.includes('json')) return null
        return (await r.json()) as { user?: User }
      })
      .then((d) => {
        if (!live || !d) return
        if ('out' in d) { cachedState = 'out'; setState('out'); return }
        if (d.user) { cachedMe = d.user; cachedState = 'in'; setMe(d.user); setState('in') }
      })
      .catch(() => {})
    return () => { live = false }
  }, [])

  return { me, state }
}

export function useSyncState(): SyncState {
  const [s, setS] = useState<SyncState>({ status: 'idle', pending: 0 })
  useEffect(() => subscribe(setS), [])
  return s
}
